/**
 * Robust PWA update helper.
 *
 * The goal: when the user clicks "עדכן" we MUST land on the latest frontend
 * version, even if the Service Worker is broken, missing, or stuck. We do this
 * in three layers, each a fallback for the previous one:
 *
 *  1. Ask the registered SW to skip waiting and reload (the happy path used by
 *     vite-plugin-pwa's `updateServiceWorker(true)`).
 *  2. Manually unregister every controller-bound SW.
 *  3. Drop every browser cache and force a hard reload of `/`.
 *
 * A short watchdog timer also calls `location.reload()` so a hung SW never
 * leaves the user stranded on a stale page.
 */

export async function clearAllCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const names = await caches.keys();
    await Promise.all(names.map((n) => caches.delete(n)));
  } catch (err) {
    console.warn('clearAllCaches failed', err);
  }
}

export async function unregisterAllSW(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
  } catch (err) {
    console.warn('unregisterAllSW failed', err);
  }
}

/**
 * Trigger the update flow. If a vite-plugin-pwa `updateServiceWorker` is
 * available, prefer it (it sends SKIP_WAITING + reloads). Otherwise fall back
 * to a manual cache-bust + reload.
 */
export async function applyUpdate(
  updateServiceWorker?: (reloadPage?: boolean) => Promise<void> | void,
): Promise<void> {
  // Watchdog: regardless of what the SW does, we force a reload after 4s.
  const watchdog = window.setTimeout(() => {
    forceReload();
  }, 4000);

  try {
    if (typeof updateServiceWorker === 'function') {
      try {
        await updateServiceWorker(true);
        // updateServiceWorker(true) reloads the page itself; if we reach here,
        // it didn't — fall through to the manual path.
      } catch (err) {
        console.warn('updateServiceWorker failed, falling back', err);
      }
    }

    await clearAllCaches();
    await unregisterAllSW();
  } finally {
    window.clearTimeout(watchdog);
    forceReload();
  }
}

function forceReload(): void {
  // Use a URL query bust so any CDN/proxy in front of nginx serves a fresh
  // index.html. The application itself ignores `_v` (we don't read it).
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}
